// One client interface for the tab, backed by the real API or, when the
// backend routes aren't deployed yet AND sample data is allowed, by sample
// data. The mode is decided once, on the first load, so the tab never mixes
// real and sample records. Only a missing route can lead to sample data:
// any other error (network, auth, server) is shown as an error.
import {
    QcBackendUnavailable,
    createQcUpload,
    createQcVideo,
    decideQc,
    extractQcChecklist,
    getQcHistory,
    getQcOverview,
    getQcVideo,
    runQc,
    saveQcBrief,
    uploadQcFile,
} from 'api_handlers/videoQc';
import { createSampleQc } from './sampleQc';

export function createLiveQc(campaignId) {
    return {
        mode: 'live',
        overview: () => getQcOverview(campaignId),
        saveBrief: (input) => saveQcBrief(campaignId, input),
        extractChecklist: () => extractQcChecklist(campaignId),
        createVideo: (input) => createQcVideo(campaignId, input),
        getVideo: (id) => getQcVideo(campaignId, id),
        getHistory: (id) => getQcHistory(campaignId, id),
        runQc: (id) => runQc(campaignId, id),
        decide: (id, input) => decideQc(campaignId, id, input),
        async upload(file, { signal } = {}) {
            const upload = await createQcUpload(campaignId, { filename: file.name, content_type: file.type || 'video/mp4' });
            if (upload.max_bytes && file.size > upload.max_bytes) {
                throw new Error(`File is over the ${Math.round(upload.max_bytes / 1048576)} MB limit`);
            }
            return uploadQcFile(upload, file, { signal });
        },
    };
}

// Returns { client, overview }, or { unavailable: true } when the backend
// isn't deployed and sample data is not allowed here.
export async function loadQc(campaignId, campaignVideos, { allowSample = false, live = createLiveQc(campaignId) } = {}) {
    try {
        return { client: live, overview: await live.overview() };
    } catch (err) {
        if (!(err instanceof QcBackendUnavailable)) throw err;
        if (!allowSample) return { unavailable: true };
        const sample = createSampleQc(campaignId, campaignVideos);
        return { client: sample, overview: await sample.overview() };
    }
}

const FALLBACK = 'Something went wrong. Please retry.';
const isText = (v) => typeof v === 'string' && v.trim() !== '';

// Always a string, whatever shape the error came in: our envelope
// ({ error: { message } }), FastAPI ({ detail: "..." } or a 422 list of
// { msg }), or an Error. Rendering a non-string here would crash the page.
export function errorText(err) {
    const candidates = [err?.error?.message, err?.detail, err?.message];
    const found = candidates.find(isText);
    if (found) return found;
    if (Array.isArray(err?.detail)) {
        const msgs = err.detail.map((d) => d?.msg).filter(isText);
        if (msgs.length) return msgs.join('; ');
    }
    return FALLBACK;
}

// API-supplied text that we render. Anything that isn't a string is dropped.
export const asText = (value, fallback = '') => (typeof value === 'string' ? value : fallback);
